import { supabase } from '../lib/supabaseClient';
import type { Action } from '../types/action';

/**
 * Upserts a plan action record to the `actions` Supabase table
 * so it can be queried cross-project by assigned user (Minha Mesa).
 */
export async function syncPlanActionToSupabase(
  action: Action,
  organizationId: string,
  projectId: string,
  responsibleUserId: string,
): Promise<void> {
  if (!organizationId || !projectId || !responsibleUserId) return;

  const statusMap: Record<string, string> = {
    todo: 'pending',
    doing: 'in_progress',
    done: 'completed',
    canceled: 'cancelled',
  };

  const priorityMap: Record<number, string> = {
    1: 'low',
    2: 'medium',
    3: 'high',
  };

  const payload = {
    id: action.id,
    organization_id: organizationId,
    project_id: projectId,
    title: action.title,
    description: action.description ?? null,
    status: statusMap[action.status] ?? 'pending',
    priority: action.impacto != null ? (priorityMap[action.impacto] ?? 'medium') : 'medium',
    due_date: action.prazo ? (() => {
      // Handle DD/MM/YYYY format
      if (action.prazo.includes('/')) {
        const [d, m, y] = action.prazo.split('/');
        return `${y}-${m}-${d}`;
      }
      return action.prazo.slice(0, 10);
    })() : null,
    assigned_to: responsibleUserId,
    updated_at: new Date().toISOString(),
  };

  await supabase
    .from('actions')
    .upsert(payload, { onConflict: 'id' });
}

/**
 * Loads plan actions assigned to a specific user from the `actions` table.
 * Used by Minha Mesa to show cross-project assigned actions.
 */
export async function loadMyPlanActions(
  userId: string,
  organizationId: string,
): Promise<AssignedPlanAction[]> {
  if (!userId || !organizationId) return [];

  const { data, error } = await supabase
    .from('actions')
    .select('id, title, status, due_date, project_id, priority, created_at, updated_at')
    .eq('organization_id', organizationId)
    .eq('assigned_to', userId)
    .not('status', 'in', '(completed,cancelled)')
    .order('due_date', { ascending: true, nullsFirst: false })
    .limit(50);

  if (error || !data) return [];

  return data.map((row: Record<string, unknown>) => ({
    id: row.id as string,
    title: row.title as string,
    status: row.status as string,
    dueDate: row.due_date as string | null,
    projectId: row.project_id as string | null,
    priority: row.priority as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }));
}

export interface AssignedPlanAction {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
  projectId: string | null;
  priority: string | null;
  createdAt: string;
  updatedAt: string;
}
